import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useTenantConfig } from '@webmono/storefront-ui/providers'
import { formatCents, cn } from '@webmono/storefront-ui/utils'
import type { DeliveryAddress } from '@webmono/storefront-ui/types'

interface MockOrder {
  id: string
  date: string
  items: { name: string; quantity: number }[]
  totalCents: number
  status: 'completed' | 'cancelled'
}

const MOCK_CUSTOMER = {
  name: 'Alex Chen',
  email: 'alex.chen@email.com',
  phone: '(415) 555-1234',
  memberSince: '2024-03-15',
}

const MOCK_LOYALTY = {
  currentPoints: 850,
  currentTier: 'silver',
  lifetimePoints: 2350,
}

const MOCK_ORDERS: MockOrder[] = [
  {
    id: 'ord_abc123',
    date: '2024-12-28',
    items: [
      { name: 'California Roll', quantity: 2 },
      { name: 'Miso Soup', quantity: 1 },
    ],
    totalCents: 3450,
    status: 'completed',
  },
  {
    id: 'ord_def456',
    date: '2024-12-15',
    items: [
      { name: 'Salmon Sashimi', quantity: 1 },
      { name: 'Rainbow Roll', quantity: 1 },
    ],
    totalCents: 4200,
    status: 'completed',
  },
  {
    id: 'ord_ghi789',
    date: '2024-11-30',
    items: [{ name: 'Dragon Roll', quantity: 3 }],
    totalCents: 5100,
    status: 'completed',
  },
]

const MOCK_ADDRESSES: DeliveryAddress[] = [
  {
    street: '123 Market Street',
    unit: 'Apt 4B',
    city: 'San Francisco',
    province: 'CA',
    postalCode: '94102',
    country: 'USA',
    instructions: 'Ring buzzer 4B',
  },
  {
    street: '456 Mission Bay Blvd',
    city: 'San Francisco',
    province: 'CA',
    postalCode: '94158',
    country: 'USA',
  },
]

export function WalletPage() {
  const tenant = useTenantConfig()
  const [activeTab, setActiveTab] = useState<'loyalty' | 'orders' | 'addresses'>('loyalty')

  const loyaltyEnabled = tenant.features.loyalty && tenant.loyalty?.enabled
  const loyalty = tenant.loyalty

  const currentTierData = loyalty?.tiers.find((t) => t.id === MOCK_LOYALTY.currentTier)
  const nextTier = loyalty?.tiers.find((t) => t.minPoints > MOCK_LOYALTY.currentPoints)

  const progressPercent = nextTier
    ? Math.min(100, ((MOCK_LOYALTY.currentPoints - (currentTierData?.minPoints || 0)) / (nextTier.minPoints - (currentTierData?.minPoints || 0))) * 100)
    : 100

  return (
    <div className="max-w-lg mx-auto px-4 py-4 pb-24">
      <div className="flex items-center gap-4 mb-6">
        <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
          <span className="text-2xl font-bold text-primary">
            {MOCK_CUSTOMER.name.split(' ').map((n) => n[0]).join('')}
          </span>
        </div>
        <div>
          <h1 className="text-xl font-semibold text-foreground">{MOCK_CUSTOMER.name}</h1>
          <p className="text-sm text-muted-foreground">{MOCK_CUSTOMER.email}</p>
          <p className="text-xs text-muted-foreground">
            Member since {new Date(MOCK_CUSTOMER.memberSince).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
          </p>
        </div>
      </div>

      <div className="flex gap-2 mb-6">
        {loyaltyEnabled && (
          <button
            onClick={() => setActiveTab('loyalty')}
            className={cn(
              'flex-1 py-2.5 px-4 rounded-lg font-medium text-sm transition-all',
              activeTab === 'loyalty'
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-muted-foreground hover:text-foreground'
            )}
          >
            Rewards
          </button>
        )}
        <button
          onClick={() => setActiveTab('orders')}
          className={cn(
            'flex-1 py-2.5 px-4 rounded-lg font-medium text-sm transition-all',
            activeTab === 'orders'
              ? 'bg-primary text-primary-foreground'
              : 'bg-muted text-muted-foreground hover:text-foreground'
          )}
        >
          Orders
        </button>
        <button
          onClick={() => setActiveTab('addresses')}
          className={cn(
            'flex-1 py-2.5 px-4 rounded-lg font-medium text-sm transition-all',
            activeTab === 'addresses'
              ? 'bg-primary text-primary-foreground'
              : 'bg-muted text-muted-foreground hover:text-foreground'
          )}
        >
          Addresses
        </button>
      </div>

      {activeTab === 'loyalty' && loyaltyEnabled && loyalty && (
        <div className="space-y-6">
          <div className="bg-gradient-to-br from-primary to-primary/80 rounded-xl p-5 text-primary-foreground">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-sm opacity-90">{loyalty.programName}</p>
                <p className="text-3xl font-bold">{MOCK_LOYALTY.currentPoints.toLocaleString()}</p>
                <p className="text-sm opacity-75">points available</p>
              </div>
              <div className="text-right">
                <span
                  className={cn(
                    'inline-block px-3 py-1 rounded-full text-sm font-medium',
                    currentTierData?.id === 'gold' && 'bg-yellow-400/20 text-yellow-200',
                    currentTierData?.id === 'silver' && 'bg-gray-300/20 text-gray-200',
                    (currentTierData?.id === 'bronze' || currentTierData?.id === 'member') && 'bg-orange-400/20 text-orange-200'
                  )}
                >
                  {currentTierData?.name || 'Member'}
                </span>
                <p className="text-xs opacity-75 mt-1">{currentTierData?.multiplier}x points</p>
              </div>
            </div>

            {nextTier && (
              <div>
                <div className="flex justify-between text-xs mb-1">
                  <span>{currentTierData?.name}</span>
                  <span>{nextTier.name}</span>
                </div>
                <div className="h-2 bg-white/20 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-white rounded-full transition-all duration-500"
                    style={{ width: `${progressPercent}%` }}
                  />
                </div>
                <p className="text-xs opacity-75 mt-1 text-center">
                  {(nextTier.minPoints - MOCK_LOYALTY.currentPoints).toLocaleString()} points to {nextTier.name}
                </p>
              </div>
            )}
          </div>

          <div>
            <h3 className="font-medium text-foreground mb-3">Your Perks</h3>
            <div className="bg-card border border-border rounded-lg divide-y divide-border">
              {currentTierData?.perks.map((perk, i) => (
                <div key={i} className="px-4 py-3 flex items-center gap-3">
                  <svg className="w-5 h-5 text-success flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-sm text-foreground">{perk}</span>
                </div>
              ))}
            </div>
          </div>

          <div>
            <h3 className="font-medium text-foreground mb-3">Available Rewards</h3>
            <div className="space-y-2">
              {loyalty.redemptionOptions.map((option) => {
                const canAfford = MOCK_LOYALTY.currentPoints >= option.pointsCost
                return (
                  <div
                    key={option.id}
                    className={cn(
                      'bg-card border rounded-lg p-4 flex items-center justify-between',
                      canAfford ? 'border-border' : 'border-border opacity-50'
                    )}
                  >
                    <div>
                      <p className="font-medium text-foreground">{option.name}</p>
                      {option.description && (
                        <p className="text-xs text-muted-foreground">{option.description}</p>
                      )}
                    </div>
                    <div className="text-right">
                      <p className={cn('font-semibold', canAfford ? 'text-primary' : 'text-muted-foreground')}>
                        {option.pointsCost.toLocaleString()} pts
                      </p>
                      <p className="text-xs text-success">
                        Save {formatCents(option.discountCents, tenant.operations.currency)}
                      </p>
                    </div>
                  </div>
                )
              })}
            </div>
            <p className="text-xs text-muted-foreground text-center mt-3">
              Redeem rewards at checkout
            </p>
          </div>

          <div className="bg-muted/50 rounded-lg p-4 text-center">
            <p className="text-sm text-muted-foreground">
              Earn <span className="font-semibold text-foreground">{loyalty.pointsPerDollar} points</span> for every $1 spent
            </p>
          </div>
        </div>
      )}

      {activeTab === 'orders' && (
        <div className="space-y-4">
          {MOCK_ORDERS.length === 0 ? (
            <div className="text-center py-12">
              <svg className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              <p className="text-muted-foreground">No orders yet</p>
              <Link to="/menu" className="inline-block mt-4 text-primary font-medium hover:underline">
                Browse Menu
              </Link>
            </div>
          ) : (
            MOCK_ORDERS.map((order) => (
              <Link
                key={order.id}
                to={`/order/${order.id}`}
                className="block bg-card border border-border rounded-lg p-4 hover:border-primary/50 transition-colors"
              >
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <p className="text-sm font-medium text-foreground">
                      {new Date(order.date).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                      })}
                    </p>
                    <p className="text-xs text-muted-foreground">#{order.id.slice(-6).toUpperCase()}</p>
                  </div>
                  <span
                    className={cn(
                      'px-2 py-0.5 rounded-full text-xs font-medium',
                      order.status === 'completed' && 'bg-success/10 text-success',
                      order.status === 'cancelled' && 'bg-destructive/10 text-destructive'
                    )}
                  >
                    {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                  </span>
                </div>
                <p className="text-sm text-muted-foreground mb-2">
                  {order.items.map((item) => `${item.quantity}x ${item.name}`).join(', ')}
                </p>
                <p className="text-sm font-medium text-foreground">
                  {formatCents(order.totalCents, tenant.operations.currency)}
                </p>
              </Link>
            ))
          )}

          {MOCK_ORDERS.length > 0 && (
            <p className="text-xs text-muted-foreground text-center">
              Showing recent orders
            </p>
          )}
        </div>
      )}

      {activeTab === 'addresses' && (
        <div className="space-y-4">
          {MOCK_ADDRESSES.map((address, i) => (
            <div
              key={i}
              className="bg-card border border-border rounded-lg p-4"
            >
              <div className="flex items-start justify-between">
                <div>
                  <p className="font-medium text-foreground">{address.street}</p>
                  {address.unit && <p className="text-sm text-muted-foreground">{address.unit}</p>}
                  <p className="text-sm text-muted-foreground">
                    {address.city}, {address.province} {address.postalCode}
                  </p>
                  {address.instructions && (
                    <p className="text-xs text-muted-foreground mt-2 italic">
                      "{address.instructions}"
                    </p>
                  )}
                </div>
                {i === 0 && (
                  <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary">
                    Default
                  </span>
                )}
              </div>
            </div>
          ))}

          <button
            type="button"
            className="w-full py-3 border-2 border-dashed border-border rounded-lg text-muted-foreground hover:border-primary/50 hover:text-foreground transition-colors flex items-center justify-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add New Address
          </button>
        </div>
      )}
    </div>
  )
}
